import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { ArrowLeft, Trophy, Trash2, X, Search, Swords, TrendingUp, Target, Skull, Crown, History, Activity, Medal } from 'lucide-react';
import { db, APP_ID_INTERNO } from '../firebase';
import './LeagueRanking.css';

// =========================================================================
// 📊 COMPONENTE MODAL DE ESTADÍSTICAS AVANZADAS
// =========================================================================
function PlayerStatsModal({ player, tournaments, onClose }) {
    const [h2hSearch, setH2hSearch] = useState('');

    const stats = useMemo(() => {
        let s = {
            totalMatches: 0, wins: 0, losses: 0, draws: 0,
            bestPlacement: Infinity, championships: 0,
            opponents: {} // { "OPP_NAME": { wins, losses, name } }
        };

        tournaments.forEach(t => {
            if (t.phase !== 'completed' || t.isUnranked) return;

            // Mapeo de IDs a Nombres para este torneo
            const idToName = {};
            (t.players || []).forEach(p => {
                idToName[p.id] = p.name.trim().toUpperCase();
            });

            // Comprobar si participó y su puesto
            const playerResultIndex = (t.leagueResults || []).findIndex(r => r.name.trim().toUpperCase() === player.name.toUpperCase());
            if (playerResultIndex !== -1) {
                const placement = playerResultIndex + 1;
                if (placement < s.bestPlacement) s.bestPlacement = placement;
                if (placement === 1) s.championships++;
            }

            // Procesar combates
            const processMatch = (m) => {
                if (m.isKubey || m.status !== 'completed') return;
                const p1Name = idToName[m.p1];
                const p2Name = idToName[m.p2];
                
                if (!p1Name || !p2Name) return;

                const isP1 = p1Name === player.name.toUpperCase();
                const isP2 = p2Name === player.name.toUpperCase();

                if (!isP1 && !isP2) return;

                const opponentId = isP1 ? m.p2 : m.p1;
                const opponentNameUpper = isP1 ? p2Name : p1Name;
                const opponentOriginalName = t.players.find(p => p.id === opponentId)?.name || opponentNameUpper;

                if (!s.opponents[opponentNameUpper]) {
                    s.opponents[opponentNameUpper] = { wins: 0, losses: 0, draws: 0, name: opponentOriginalName };
                }

                const score1 = Number(m.p1Score);
                const score2 = Number(m.p2Score);
                
                s.totalMatches++;

                if (score1 === score2) {
                    s.draws++;
                    s.opponents[opponentNameUpper].draws++;
                } else if ((isP1 && score1 > score2) || (isP2 && score2 > score1)) {
                    s.wins++;
                    s.opponents[opponentNameUpper].wins++;
                } else {
                    s.losses++;
                    s.opponents[opponentNameUpper].losses++;
                }
            };

            (t.phase1Matches || []).forEach(processMatch);
            (t.tiebreakerMatches || []).forEach(processMatch);
            (t.phase2Bracket || []).forEach(round => {
                (round.matches || []).forEach(processMatch);
            });
        });

        return s;
    }, [player.name, tournaments]);

    const winRate = stats.totalMatches > 0 ? Math.round((stats.wins / stats.totalMatches) * 100) : 0;

    // Calcular Némesis y Víctima
    let nemesis = null;
    let victim = null;
    let maxLosses = 0;
    let maxWins = 0;

    Object.values(stats.opponents).forEach(opp => {
        if (opp.losses > maxLosses || (opp.losses === maxLosses && maxLosses > 0 && opp.losses > opp.wins)) {
            maxLosses = opp.losses;
            nemesis = opp;
        }
        if (opp.wins > maxWins || (opp.wins === maxWins && maxWins > 0 && opp.wins > opp.losses)) {
            maxWins = opp.wins;
            victim = opp;
        }
    });

    const filteredOpponents = Object.values(stats.opponents)
        .filter(opp => opp.name.toLowerCase().includes(h2hSearch.toLowerCase()))
        .sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses));

    return ReactDOM.createPortal(
        <div className="modal-overlay">
            <div className="stats-modal-content animate-fade-in">
                
                {/* CABECERA */}
                <div className="stats-header">
                    <div style={{display: 'flex', alignItems: 'center', gap: '1.5rem'}}>
                        <div className="stats-avatar">
                            <Activity style={{width: '2.5rem', height:'2.5rem'}}/>
                        </div>
                        <div>
                            <h3 className="modal-title" style={{maxWidth: '20rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '2.5rem'}}>{player.name}</h3>
                            <p className="modal-subtitle" style={{marginTop: '0.5rem'}}>
                                {stats.totalMatches} Combates Totales Registrados
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="modal-close-btn">
                        <X style={{width: '2rem', height:'2rem'}}/>
                    </button>
                </div>
                
                {/* CONTENIDO SCROLLABLE */}
                <div className="stats-body">
                    
                    {/* METRICAS PRINCIPALES */}
                    <div className="stats-grid-4">
                        <div className="stat-card">
                            <div className="stat-label"><TrendingUp style={{width:'1rem', height:'1rem'}}/> Win Rate</div>
                            <div className="stat-value">{winRate}%</div>
                            <div style={{fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight:'700', marginTop:'0.5rem'}}>{stats.wins}V - {stats.losses}D</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label"><Crown style={{width:'1rem', height:'1rem'}}/> Campeonatos</div>
                            <div className="stat-value" style={{color: 'var(--accent-yellow)'}}>{stats.championships}</div>
                            <div style={{fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight:'700', marginTop:'0.5rem'}}>Torneos ganados</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label"><Medal style={{width:'1rem', height:'1rem'}}/> Mejor Puesto</div>
                            <div className="stat-value">
                                {stats.bestPlacement === Infinity ? '-' : `${stats.bestPlacement}º`}
                            </div>
                            <div style={{fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight:'700', marginTop:'0.5rem'}}>Récord histórico</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label"><Trophy style={{width:'1rem', height:'1rem'}}/> Torneos</div>
                            <div className="stat-value" style={{color: 'var(--accent-cyan)'}}>{player.tournamentsPlayed}</div>
                            <div style={{fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight:'700', marginTop:'0.5rem'}}>Participaciones</div>
                        </div>
                    </div>

                    {/* NEMESIS Y VICTIMA */}
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem'}}>
                        <div className="nemesis-card">
                            <Skull style={{width: '4rem', height:'4rem', color:'var(--accent-red)', opacity:'0.2', position:'absolute', right:'-0.5rem', bottom:'-0.5rem', transform:'rotate(12deg)'}} />
                            <div style={{background: 'var(--accent-red)', padding:'1rem', borderRadius:'1rem', color:'#fff', zIndex:'10'}}>
                                <Skull style={{width:'2rem', height:'2rem'}} />
                            </div>
                            <div style={{zIndex:'10'}}>
                                <div style={{color:'var(--accent-red)', fontSize:'0.75rem', fontWeight:'900', textTransform:'uppercase', letterSpacing:'2px'}}>El Némesis</div>
                                <div style={{fontSize:'1.5rem', fontWeight:'900', textTransform:'uppercase', fontStyle:'italic', letterSpacing:'-1px', color:'var(--text-primary)'}}>
                                    {nemesis ? nemesis.name : 'Ninguno'}
                                </div>
                                {nemesis && <div style={{fontSize:'0.875rem', fontWeight:'700', color:'var(--accent-red)', marginTop:'0.25rem'}}>{nemesis.wins}V - {nemesis.losses}D en contra</div>}
                            </div>
                        </div>

                        <div className="victim-card">
                            <Target style={{width: '4rem', height:'4rem', color:'var(--accent-cyan)', opacity:'0.2', position:'absolute', right:'-0.5rem', bottom:'-0.5rem', transform:'rotate(-12deg)'}} />
                            <div style={{background: 'var(--accent-cyan)', padding:'1rem', borderRadius:'1rem', color:'#000', zIndex:'10'}}>
                                <Target style={{width:'2rem', height:'2rem'}} />
                            </div>
                            <div style={{zIndex:'10'}}>
                                <div style={{color:'var(--accent-cyan)', fontSize:'0.75rem', fontWeight:'900', textTransform:'uppercase', letterSpacing:'2px'}}>La Víctima</div>
                                <div style={{fontSize:'1.5rem', fontWeight:'900', textTransform:'uppercase', fontStyle:'italic', letterSpacing:'-1px', color:'var(--text-primary)'}}>
                                    {victim ? victim.name : 'Ninguna'}
                                </div>
                                {victim && <div style={{fontSize:'0.875rem', fontWeight:'700', color:'var(--accent-cyan)', marginTop:'0.25rem'}}>{victim.wins}V - {victim.losses}D a favor</div>}
                            </div>
                        </div>
                    </div>

                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem'}}>
                        {/* HISTORIAL DE PUNTOS */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                            <h4 style={{fontWeight:'900', textTransform:'uppercase', letterSpacing:'-1px', fontSize:'1.25rem', display:'flex', alignItems:'center', gap:'0.5rem', borderBottom:'1px solid var(--glass-border)', paddingBottom:'0.75rem'}}>
                                <History style={{color:'var(--accent-cyan)'}}/> Historial de Liga
                            </h4>
                            <div className="stats-list-container">
                                {player.history.sort((a, b) => b.date - a.date).map((h, hIdx) => (
                                    <div key={hIdx} className="stats-list-item">
                                        <span style={{fontWeight:'900', fontStyle:'italic', fontSize:'0.875rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{h.tournamentName}</span>
                                        <span style={{fontWeight:'900', color:'var(--accent-yellow)', background:'rgba(241,196,15,0.1)', padding:'0.25rem 0.75rem', borderRadius:'0.5rem'}}>+{h.points} pts</span>
                                    </div>
                                ))}
                                {player.history.length === 0 && (
                                    <div style={{fontSize:'0.875rem', color:'var(--text-secondary)', fontStyle:'italic', textAlign:'center', padding:'1rem'}}>No ha participado en torneos aún.</div>
                                )}
                                {player.basePoints !== 0 && (
                                    <div className="stats-list-item" style={{background: 'rgba(155, 89, 182, 0.1)'}}>
                                        <span style={{fontWeight:'900', fontStyle:'italic', fontSize:'0.875rem', color:'#c39bd3'}}>Puntos Base (Gestión)</span>
                                        <span style={{fontWeight:'900', color:'#c39bd3', background:'rgba(155, 89, 182, 0.2)', padding:'0.25rem 0.75rem', borderRadius:'0.5rem'}}>{player.basePoints > 0 ? `+${player.basePoints}` : player.basePoints} pts</span>
                                    </div>
                                )}
                                {player.manualAdjustment !== 0 && (
                                    <div className="stats-list-item" style={{background: 'rgba(52, 152, 219, 0.1)'}}>
                                        <span style={{fontWeight:'900', fontStyle:'italic', fontSize:'0.875rem', color:'#85c1e9'}}>Ajuste Manual Extra</span>
                                        <span style={{fontWeight:'900', color:'#85c1e9', background:'rgba(52, 152, 219, 0.2)', padding:'0.25rem 0.75rem', borderRadius:'0.5rem'}}>{player.manualAdjustment > 0 ? `+${player.manualAdjustment}` : player.manualAdjustment} pts</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* BUSCADOR H2H */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                            <h4 style={{fontWeight:'900', textTransform:'uppercase', letterSpacing:'-1px', fontSize:'1.25rem', display:'flex', alignItems:'center', gap:'0.5rem', borderBottom:'1px solid var(--glass-border)', paddingBottom:'0.75rem'}}>
                                <Swords style={{color:'var(--accent-yellow)'}}/> Cara a Cara (H2H)
                            </h4>
                            <div className="stats-list-container" style={{height: '300px', display: 'flex', flexDirection: 'column'}}>
                                <div style={{display:'flex', alignItems:'center', gap:'0.5rem', background:'rgba(0,0,0,0.5)', padding:'0.75rem', borderRadius:'0.75rem', marginBottom:'1rem'}}>
                                    <Search style={{width:'1.25rem', height:'1.25rem', color:'var(--text-secondary)'}} />
                                    <input 
                                        type="text" 
                                        value={h2hSearch}
                                        onChange={e => setH2hSearch(e.target.value)}
                                        placeholder="Buscar oponente..."
                                        style={{background:'transparent', border:'none', outline:'none', color:'var(--text-primary)', width:'100%', fontWeight:'700', fontFamily:'var(--font-body)'}}
                                    />
                                </div>
                                <div style={{overflowY: 'auto', flex: 1}}>
                                    {filteredOpponents.map((opp, idx) => {
                                        const total = opp.wins + opp.losses + opp.draws;
                                        const oppWinRate = Math.round((opp.wins / total) * 100);
                                        return (
                                        <div key={idx} className="stats-list-item">
                                            <span style={{fontWeight:'900', fontStyle:'italic', textTransform:'uppercase', fontSize:'0.875rem'}}>{opp.name}</span>
                                            <div style={{display:'flex', alignItems:'center', gap:'0.75rem'}}>
                                                <div style={{display:'flex', gap:'0.25rem', fontSize:'0.75rem', fontWeight:'900'}}>
                                                    <span style={{color:'var(--accent-cyan)', background:'var(--status-win)', padding:'0.1rem 0.4rem', borderRadius:'0.25rem'}}>{opp.wins}V</span>
                                                    <span style={{color:'var(--accent-red)', background:'var(--status-loss)', padding:'0.1rem 0.4rem', borderRadius:'0.25rem'}}>{opp.losses}D</span>
                                                </div>
                                                <div style={{width:'2.5rem', textAlign:'right', fontSize:'0.75rem', fontWeight:'900', color: oppWinRate >= 50 ? 'var(--accent-cyan)' : 'var(--accent-red)'}}>
                                                    {oppWinRate}%
                                                </div>
                                            </div>
                                        </div>
                                    )})}
                                    {filteredOpponents.length === 0 && (
                                        <div style={{fontSize:'0.875rem', color:'var(--text-secondary)', fontStyle:'italic', textAlign:'center', padding:'1rem'}}>No hay combates encontrados.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>,
        document.body
    );
}

// =========================================================================
// 🌍 RANKING GLOBAL DE LIGA (COMPONENTE PRINCIPAL)
// =========================================================================
export default function LeagueRanking({ tournaments, onBack, role }) {
    const [manualAdjustments, setManualAdjustments] = useState({});
    const [basePointsData, setBasePointsData] = useState({});
    const [selectedPlayerForStats, setSelectedPlayerForStats] = useState(null);

    useEffect(() => {
        const adjRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'manuales');
        const unsubscribe = onSnapshot(adjRef, (docSnap) => {
            setManualAdjustments(docSnap.exists() ? docSnap.data() : {});
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
        const unsubscribe = onSnapshot(bpRef, (docSnap) => {
            setBasePointsData(docSnap.exists() ? docSnap.data() : {});
        });
        return () => unsubscribe();
    }, []);

    const ranking = useMemo(() => {
        const totals = {};
        
        tournaments.filter(t => t.phase === 'completed' && !t.isUnranked).forEach(t => {
            (t.leagueResults || []).forEach(r => {
                const nameKey = r.name.trim().toUpperCase();
                if (manualAdjustments[nameKey]?.deleted) return;

                if (!totals[nameKey]) {
                    totals[nameKey] = { name: r.name.trim(), total: 0, tournamentsPlayed: 0, manualAdjustment: 0, basePoints: 0, history: [] };
                }
                totals[nameKey].total += r.total;
                totals[nameKey].tournamentsPlayed += 1;
                
                totals[nameKey].history.push({
                    tournamentName: t.name,
                    points: r.total,
                    date: t.createdAt
                });
            });
        });

        Object.keys(basePointsData).forEach(nameKey => {
            const bp = basePointsData[nameKey];
            if (manualAdjustments[nameKey]?.deleted) return;

            if (!totals[nameKey]) {
                totals[nameKey] = { name: bp.originalName, total: 0, tournamentsPlayed: 0, manualAdjustment: 0, basePoints: 0, history: [] };
            }
            totals[nameKey].basePoints = bp.points || 0;
            totals[nameKey].total += bp.points || 0;
        });

        Object.keys(manualAdjustments).forEach(nameKey => {
            const adj = manualAdjustments[nameKey];
            if (adj.deleted) return;

            if (!totals[nameKey]) {
                totals[nameKey] = { name: adj.originalName, total: 0, tournamentsPlayed: 0, manualAdjustment: 0, basePoints: 0, history: [] };
            }
            totals[nameKey].manualAdjustment = adj.points || 0;
            totals[nameKey].total += adj.points || 0;
        });
        
        return Object.values(totals).sort((a, b) => b.total - a.total);
    }, [tournaments, manualAdjustments, basePointsData]);

    const updateAdjustment = async (nameKey, originalName, newPoints) => {
        if (role !== 'admin') return;
        const adjRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'manuales');
        await setDoc(adjRef, {
            [nameKey]: { originalName, points: newPoints, deleted: false }
        }, { merge: true });
    };

    const handleAdjustClick = (e, r) => {
        e.stopPropagation();
        if (role !== 'admin') return;
        const nameKey = r.name.trim().toUpperCase();
        const currentAdj = manualAdjustments[nameKey]?.points || 0;
        const input = window.prompt(`Ajuste manual para ${r.name}. (Ej: 5 o -2)\nPuntos de ajuste actuales: ${currentAdj}`, currentAdj);
        
        if (input === null) return;
        const points = parseInt(input, 10);
        if (isNaN(points)) return window.alert("Por favor, introduce un número válido.");
        
        updateAdjustment(nameKey, r.name, points);
    };

    const handleDeletePlayer = async (e, r) => {
        e.stopPropagation();
        if (role !== 'admin') return;
        if (!window.confirm(`¿Estás seguro de que quieres ocultar a ${r.name} del ranking global?\n\n(Sus resultados se mantendrán en el histórico, pero no aparecerá en esta tabla).`)) return;
        
        const nameKey = r.name.trim().toUpperCase();
        const adjRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'manuales');
        await setDoc(adjRef, {
            [nameKey]: { originalName: r.name, deleted: true }
        }, { merge: true });
    };

    return (
        <div className="ranking-container">
            {selectedPlayerForStats && (
                <PlayerStatsModal 
                    player={selectedPlayerForStats} 
                    tournaments={tournaments} 
                    onClose={() => setSelectedPlayerForStats(null)} 
                />
            )}

            <div className="ranking-top-bar">
                <button onClick={onBack} className="btn btn-secondary">
                    <ArrowLeft className="w-4 h-4"/> Volver
                </button>
            </div>

            <div className="ranking-header">
                <Trophy style={{width: '5rem', height: '5rem', color: 'var(--accent-yellow)', filter: 'drop-shadow(0 0 10px rgba(241,196,15,0.5))'}} />
                <h2 className="ranking-header-title">Ranking Global</h2>
                <p className="ranking-header-subtitle">Liga Alicantina Beyblade</p>
            </div>

            <div className="ranking-table-container">
                <div className="ranking-table-scroller">
                    <table className="ranking-table">
                        <thead>
                            <tr>
                                <th>Pos</th>
                                <th>Blader</th>
                                <th title="Torneos jugados">Torn.</th>
                                <th title="Puntos Base asignados en Gestión">Base</th>
                                {role === 'admin' && <th>Ajuste</th>}
                                <th style={{color: 'var(--accent-yellow)'}}>Puntos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranking.length === 0 && (
                                <tr>
                                    <td colSpan={role === 'admin' ? "6" : "5"} style={{textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)'}}>
                                        Aún no hay torneos completados en la liga.
                                    </td>
                                </tr>
                            )}
                            {ranking.map((r, i) => (
                                <tr 
                                    key={r.name}
                                    onClick={() => setSelectedPlayerForStats(r)}
                                    className="ranking-row"
                                    title="Haz clic para ver estadísticas avanzadas"
                                >
                                    <td className="ranking-cell-center">
                                        <div className={`pos-badge ${i === 0 ? 'pos-1' : i === 1 ? 'pos-2' : i === 2 ? 'pos-3' : 'pos-other'}`}>
                                            {i + 1}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ranking-player-name">
                                            {r.name}
                                            <Activity className="hover-icon"/>
                                        </div>
                                    </td>
                                    <td className="ranking-cell-center text-secondary">{r.tournamentsPlayed}</td>
                                    <td className="ranking-cell-center" style={{color: 'var(--text-secondary)'}}>
                                        {r.basePoints > 0 ? `+${r.basePoints}` : r.basePoints !== 0 ? r.basePoints : '-'}
                                    </td>
                                    {role === 'admin' && (
                                        <td className="ranking-cell-center" onClick={(e) => e.stopPropagation()}>
                                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'}}>
                                                <span style={{fontSize: '0.75rem', fontWeight: '900', color: r.manualAdjustment > 0 ? 'var(--accent-cyan)' : r.manualAdjustment < 0 ? 'var(--accent-red)' : 'var(--text-secondary)'}}>
                                                    {r.manualAdjustment > 0 ? `+${r.manualAdjustment}` : r.manualAdjustment !== 0 ? r.manualAdjustment : '-'}
                                                </span>
                                                <div style={{display: 'flex', gap: '0.25rem'}}>
                                                    <button onClick={(e) => handleAdjustClick(e, r)} style={{background: 'rgba(255,255,255,0.1)', color: 'var(--text-primary)', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.6rem'}}>
                                                        +/-
                                                    </button>
                                                    <button onClick={(e) => handleDeletePlayer(e, r)} style={{background: 'var(--status-loss)', color: 'var(--accent-red)', border: '1px solid var(--status-loss-border)', padding: '0.25rem', borderRadius: '4px', cursor: 'pointer'}}>
                                                        <Trash2 style={{width: '0.75rem', height: '0.75rem'}} />
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    <td className="points-cell">
                                        {r.total}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}